import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { useContext } from "react";
import { BsTrash } from "react-icons/bs";
import formatShipmentProductName from "../inventory/warehouses/shipments/formatShipmentProductName";
import { OrderContext } from "./OrderDetail";
import StockCategoryProductSelector from "./StockCategoryProductSelector";

const OrderLineView = ({
  line,
  odooCurrency,
  autoFocus,
  onLineSelectorBlur,
  odooProducts,
}) => {
  const { originalOrder, order, setOrder, productStocks } =
    useContext(OrderContext);

  function selectProduct(product) {
    if (!product?.id) return;
    const foundPrice = originalOrder.pricelist
      ? originalOrder.pricelist.latestPrices.find(
          (i) => `${i.productId}` === `${product.id}`,
        )
      : null;
    onChangeLine({
      product,
      productId: product.id,
      productNumber: product.number || "",
      productName: product.name || "",
      countryCodeOfOrigin: product.countryCodeOfOrigin || "",
      version: product.version || "",
      itemsPerSet: product.itemsPerSet ?? null,
      isNew: false,
      salesPrice: foundPrice ? foundPrice.price : 0,
    });
  }

  function onChangeLine(values) {
    const newLine = { ...line, ...values };
    setOrder((prev) => ({
      ...prev,
      orderLines: prev.orderLines.map((i) => (i.id === line.id ? newLine : i)),
    }));
  }

  function removeLine() {
    setOrder((prev) => ({
      ...prev,
      orderLines: prev.orderLines.filter((i) => i.id !== line.id),
    }));
  }

  const hasQty = line.qty !== "" && line.qty !== null && line.qty !== undefined;
  const qty = Number(line.qty);
  const itemsPerSet = Number(line.product?.itemsPerSet || 0);
  const invalidatedByItemsPerSet =
    !!line.product &&
    itemsPerSet > 0 &&
    hasQty &&
    !Number.isNaN(qty) &&
    qty % itemsPerSet !== 0;
  const lineProductId = `${line.product?.id || line.productId || ""}`;
  const lineIndex = order.orderLines.findIndex((i) => i.id === line.id);
  const consumedBeforeCurrentLine = lineProductId
    ? order.orderLines.slice(0, lineIndex).reduce((sum, i) => {
        const productId = `${i.product?.id || i.productId || ""}`;
        if (productId !== lineProductId) return sum;
        const lineQty = Number(i.qty);
        return sum + (Number.isFinite(lineQty) ? lineQty : 0);
      }, 0)
    : 0;
  const stockBeforeCurrentLine = lineProductId
    ? Number(productStocks[lineProductId] ?? 0) - consumedBeforeCurrentLine
    : null;

  function buildLineProductView(line) {
    const productId = line?.product?.id || line?.productId;
    if (!productId) return null;
    const product = {
      number: line?.product?.number || line?.productNumber,
      name: line?.product?.name || line?.productName,
      countryCodeOfOrigin:
        line?.product?.countryCodeOfOrigin || line?.countryCodeOfOrigin,
      version: line?.product?.version || line?.version,
      itemsPerSet: line?.product?.itemsPerSet ?? line?.itemsPerSet,
    };
    const text = formatShipmentProductName(product);
    if (!text) return null;
    return <div>{text}</div>;
  }

  const priceAfterDiscounnt =
    parseFloat(line.salesPrice) *
    ((100 - parseFloat(line.discountPercentage)) / 100);

  const cost = odooProducts[line.product?.odooId]
    ? odooProducts[line.product.odooId]
    : null;
  let margin, marginPercentage;

  if (cost) {
    margin = priceAfterDiscounnt - cost;
    marginPercentage = (margin / priceAfterDiscounnt) * 100;
  }

  const totalPriceAfterDiscount =
    parseInt(line.qty) *
    parseFloat(line.salesPrice) *
    ((100 - parseFloat(line.discountPercentage)) / 100);

  return (
    <tr>
      <td className="w-full">
        <div className="flex space-x-2">
          <Button danger onClick={removeLine}>
            <BsTrash />
          </Button>

          <StockCategoryProductSelector
            stockCategoryId={originalOrder.stockCategory?.id}
            priceListId={originalOrder.pricelist?.id}
            valueView={buildLineProductView(line)}
            optionView={(product) => (
              <div>
                {formatShipmentProductName(product, productStocks[product.id])}
              </div>
            )}
            onSelect={selectProduct}
            autoFocus={autoFocus}
            onBlur={onLineSelectorBlur}
          />
        </div>
      </td>
      <td className="text-right">
        {stockBeforeCurrentLine === null ? "-" : stockBeforeCurrentLine}
      </td>
      <td className="text-right">
        <Input
          className="text-right w-20"
          value={line.qty}
          onChange={(e) => onChangeLine({ qty: e.target.value })}
          validated={!invalidatedByItemsPerSet}
        />
      </td>
      <td className="text-right">
        <Input
          className="text-right w-20"
          value={line.salesPrice}
          onChange={(e) => onChangeLine({ salesPrice: e.target.value })}
        />
      </td>
      <td className="text-right">
        <Input
          className="text-right w-20"
          value={line.discountPercentage}
          onChange={(e) => onChangeLine({ discountPercentage: e.target.value })}
        />
      </td>
      <td className="text-right">
        {priceAfterDiscounnt.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td className="text-right">
        {margin ? (
          <span className="whitespace-nowrap">
            {(margin * parseInt(line.qty)).toFixed(2)} (
            {marginPercentage.toFixed(2)}%)
          </span>
        ) : (
          "-"
        )}
      </td>
      <td className="text-right">
        {totalPriceAfterDiscount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>

      {odooCurrency && (
        <>
          <td className="text-right">
            {(parseFloat(line.salesPrice) * order.exchangeRate).toLocaleString(
              undefined,
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              },
            )}
          </td>
          <td className="text-right">
            {(
              parseInt(line.qty) *
              parseFloat(line.salesPrice) *
              ((100 - parseFloat(line.discountPercentage)) / 100) *
              order.exchangeRate
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </td>
        </>
      )}
    </tr>
  );
};

export default OrderLineView;
